import { useTacticHooks } from "../../providers/TacticHooksProvider";
import { IResource } from "../../../../api";
import { logError, useBooleanState } from "../../../../helpers";
import { useAuthenticated } from "../authenticated/useAuthenticated";

export const useSaveResourceFavorite = () => {
  const { api } = useTacticHooks();
  const isLoading = useBooleanState(false);
  const { queryClient } = useTacticHooks();
  const {
    userContext: { currentOffice }
  } = useAuthenticated();

  const invalidateQueries = async (resourceId?: string) => {
    await queryClient.invalidateQueries({
      queryKey: ["organizations.resourcesAll", currentOffice.id]
    });
    if (resourceId)
      await queryClient.invalidateQueries({
        queryKey: ["resource", resourceId]
      });
  };

  const saveResourceFavorite = async (resourceId: string) => {
    try {
      const result = await api.client.favorites.addResourceFavorite(resourceId);
      await invalidateQueries(resourceId);

      isLoading.false();
      return result;
    } catch (error) {
      logError("Error saving resource favorite:", error);
      throw error;
    } finally {
      await invalidateQueries(resourceId);
      isLoading.false();
    }
  };

  const toggleResourceFavorite = async (resource: Partial<IResource>) => {
    if (!resource.id) {
      throw new Error("Resource ID is required to toggle favorite status");
    }

    if (resource.favorite_id) {
      try {
        const result = await api.client.favorites.deleteFavorite(
          resource.favorite_id
        );
        await invalidateQueries(resource.id);
        isLoading.false();
        return result;
      } catch (error) {
        logError("Error removing resource favorite:", error);
        throw error;
      } finally {
        await invalidateQueries(resource.id);
        isLoading.false();
      }
    }

    try {
      const result = await api.client.favorites.addResourceFavorite(
        resource.id
      );
      await invalidateQueries(resource.id);
      isLoading.false();
      return result;
    } catch (error) {
      logError("Error adding resource favorite:", error);
      throw error;
    } finally {
      await invalidateQueries(resource.id);
      isLoading.false();
    }
  };

  return {
    saveResourceFavorite,
    toggleResourceFavorite,
    isLoading: isLoading.state
  };
};
